import React, { useMemo, useState, useEffect, useRef } from "react";
import axios from "../../helper/axios";

export default function StoreHeaderBanner(props) {
    const brandSlug = (props.slug || "").trim().toLowerCase();

    const [remoteBanners, setRemoteBanners] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let mounted = true;

        const fetchBrandBanners = async () => {
            if (!brandSlug) {
                if (mounted) {
                    setRemoteBanners([]);
                    setLoaded(true);
                }
                return;
            }

            setLoaded(false);
            try {
                const res = await axios.get("/getbrandbanners", {
                    params: { slug: brandSlug }
                });
                const list = Array.isArray(res.data) ? res.data : [];
                if (mounted) setRemoteBanners(list);
            } catch (err) {
                console.warn("Failed to fetch brand banners:", err?.message || err);
                if (mounted) setRemoteBanners([]);
            } finally {
                if (mounted) setLoaded(true);
            }
        };

        fetchBrandBanners();
        return () => {
            mounted = false;
        };
    }, [brandSlug]);

    const banners = useMemo(() => {
        return remoteBanners.map((b) => b.image).filter(Boolean);
    }, [remoteBanners]);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [imgError, setImgError] = useState([]);
    const [isTransitioning, setIsTransitioning] = useState(true);
    const containerRef = useRef(null);
    const autoSlideTimerRef = useRef(null);

    useEffect(() => {
        setCurrentIndex(0);
        setIsTransitioning(true);
        setImgError(new Array(banners.length).fill(false));
    }, [banners.length, brandSlug]);

    useEffect(() => {
        if (banners.length <= 1) return undefined;

        const timer = setInterval(() => {
            setCurrentIndex((i) => (i + 1) % banners.length);
        }, 6000);

        return () => clearInterval(timer);
    }, [banners.length, currentIndex]);

    const goTo = (index) => {
        if (banners.length === 0) return;
        setIsTransitioning(true);
        setCurrentIndex((index + banners.length) % banners.length);
    };

    useEffect(() => {
        const slider = containerRef.current?.querySelector(".slides-row");
        if (!slider || !containerRef.current) return;
        const width = containerRef.current.clientWidth || 1;
        slider.style.transition = isTransitioning ? "transform 500ms ease" : "none";
        slider.style.transform = `translateX(${-currentIndex * width}px)`;
    }, [currentIndex, isTransitioning, banners.length]);

    // Only show banners from DB — nothing if empty or still loading
    if (!loaded || banners.length === 0) {
        return null;
    }

    return (
        <div className="relative w-full overflow-hidden select-none" ref={containerRef}>
            <div className="slides-row flex will-change-transform">
                {banners.map((image, index) => (
                    <div key={index} className="w-full flex-shrink-0 h-48 md:h-64 lg:h-72 relative">
                        <img
                            src={image}
                            alt={`Banner ${index + 1}`}
                            onError={() => {
                                const newErrors = [...imgError];
                                newErrors[index] = true;
                                setImgError(newErrors);
                            }}
                            className="w-full h-full object-cover object-center transform transition-transform duration-700 hover:scale-105"
                            draggable={false}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                    </div>
                ))}
            </div>

            {banners.length > 1 && (
                <>
                    <div className="absolute bottom-4 left-6 gap-2 z-30 hidden md:flex">
                        {banners.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => goTo(index)}
                                className={`w-3 h-3 rounded-full transition-all ${index === currentIndex ? "bg-white w-6" : "bg-white/50 hover:bg-white/70"}`}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>

                    <button
                        onClick={() => goTo(currentIndex - 1)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full z-40 shadow-md"
                        aria-label="Previous slide"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>

                    <button
                        onClick={() => goTo(currentIndex + 1)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full z-40 shadow-md"
                        aria-label="Next slide"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                    </button>
                </>
            )}
        </div>
    );
}
